import { get, getNestedElement } from './utils';
import { AdminInput, AdminCheckbox, AdminSelect } from '@project/components';
import MeasurementInput from '../components/MeasurementInput/MeasurementInput';
import React from 'react';
import { intl } from './intl';
import styles from './renderFields.module.css';
import SurveyableField from '../components/SurveyableField/SurveyableField';
import SpaceProfilePhotoUpload from '../components/BuildForm/SpaceProfilePhotoUpload';
import ViewProfilePhoto from '../components/ViewProfilePhoto/ViewProfilePhoto';

const t = (id) => intl.formatMessage({ id });

const isFieldRequired = (key, fieldSettings) => !!fieldSettings && get(fieldSettings, `${key}.required`, false);
const isFieldSoftRequired = (key, fieldSettings) => !!fieldSettings && get(fieldSettings, `${key}.softRequired`, false);
const isFieldSurveyed = (key, fieldSettings) => !!fieldSettings && get(fieldSettings, `${key}.surveyed`, false);
const isFieldPhotosEnabled = (key, fieldSettings) => !!fieldSettings && get(fieldSettings, `${key}.photosEnabled`, false);

const isFieldEnabled = (key, defaultEnabled, fieldSettings) => {
  // check for default visiblity based on the value set in BasicInfoForm/schema.js
  const defaultFieldVisibility = defaultEnabled === false ? false : true;
  return fieldSettings && get(fieldSettings, key) ? get(fieldSettings, `${key}.enabled`, true) : defaultFieldVisibility;
}

const renderMeasurementInput = (key, form, formSchema, fieldSettings, updateField, saveForm, isReadOnly, errors, setErrors, spaceProfilePhotos, setSpaceProfilePhotos) => {
  const schema = get(formSchema, key);
  if (!schema) console.error(`renderMeasurementInput - No schema found`, { key, formSchema, form })

  const formKey = key.split('.').slice(0, key.split('.').length - 1).join('.');
  if (!isFieldEnabled(key, schema.enabled, fieldSettings)) return null;

  const required = fieldSettings ? isFieldRequired(key, fieldSettings) || isFieldSoftRequired(key, fieldSettings) : schema.required;

  return (
    <>
      <SurveyableField form={form}
        formKey={formKey}
        isReadOnly={isReadOnly}
        isSurveyed={isFieldSurveyed(key, fieldSettings)}
        fieldKey={key}
        updateField={updateField}
        saveForm={saveForm}>
        <MeasurementInput
          id={key}
          schemaKey={key}
          base={get(form, formKey)}
          form={form}
          isReadOnly={isReadOnly}
          required={required}
          errors={errors}
          updateField={updateField}
          saveForm={saveForm}
          customstyle={'mt-3'}
        />
        {isFieldPhotosEnabled(key, fieldSettings) && <SpaceProfilePhotoUpload isReadOnly={isReadOnly} guid={form.guid} fieldKey={key} spaceProfilePhotos={spaceProfilePhotos} setSpaceProfilePhotos={setSpaceProfilePhotos} />}
      </SurveyableField>
    </>
  );
}

const renderBoolean = (key, form, formSchema, fieldSettings, updateField, saveForm, isReadOnly, errors, setErrors, spaceProfilePhotos, setSpaceProfilePhotos) => {
  const schema = get(formSchema, key);
  if (!schema) console.error(`renderBoolean - No schema found`, { key, formSchema, form })

  if (!isFieldEnabled(key, schema.enabled, fieldSettings)) return null;
  const fieldKey = key.split('.').pop();
  const formKey = key.split('.').slice(0, key.split('.').length - 1).join('.');

  return (
    <>
      <AdminCheckbox
        id={key}
        label={schema.label}
        checked={get(form, key)} readOnly={isReadOnly}
        onChange={e => {
          updateField(fieldKey, e, get(form, formKey), null, key);
          saveForm(true);
        }}
        customLabelStyle={styles.adminCheckboxLabel}
        customstyle={styles.checkbox}
      />
      {isFieldPhotosEnabled(key, fieldSettings) && <SpaceProfilePhotoUpload isReadOnly={isReadOnly} guid={form.guid} fieldKey={key} spaceProfilePhotos={spaceProfilePhotos} setSpaceProfilePhotos={setSpaceProfilePhotos} />}
    </>
  );
}

const renderSelect = (key, form, formSchema, fieldSettings, updateField, saveForm, isReadOnly, errors, setErrors, spaceProfilePhotos, setSpaceProfilePhotos) => {
  const schema = get(formSchema, key);
  if (!schema) console.error(`renderSelect - No schema found`, { key, formSchema, form })

  const options = schema.options
  if (!isFieldEnabled(key, schema.enabled, fieldSettings)) return null;
  const fieldKey = key.split('.').pop();
  const formKey = key.split('.').slice(0, key.split('.').length - 1).join('.');
  const required = fieldSettings ? isFieldRequired(key, fieldSettings) || isFieldSoftRequired(key, fieldSettings) : schema.required;
  const hasError = Array.isArray(errors) && !!errors.find(_ => _.key === key);

  // if it's a multiselect, then convert the comma separated list into an array
  const values = schema.isMulti ? get(form, key)?.split(', ') : [get(form, key)];

  return (
    <SurveyableField form={form}
      formKey={formKey}
      isReadOnly={isReadOnly}
      isSurveyed={isFieldSurveyed(key, fieldSettings)}
      fieldKey={key}
      updateField={updateField}
      saveForm={saveForm}>
      <AdminSelect
        id={key}
        label={required ? `${schema.label} *` : `${schema.label}`}
        options={options}
        isMulti={schema.isMulti}
        value={options.filter(option => values?.includes(option.value)).sort((a, b) => a.value.localeCompare(b.value))}
        readOnly={isReadOnly}
        onChange={e => {
          // store the value as a comma separated list (for display purposes)
          const value = schema.isMulti ?
            e.map(option => option.value).sort().join(', ')
            : e.value;
          const formValue = { target: { type: 'select', value, checked: false } }
          updateField(fieldKey, formValue, get(form, formKey), false, key)
          saveForm(true);
        }}
        isDisabled={isReadOnly}
        isInvalid={hasError}
        placeholder={isReadOnly ? null : schema.placeholder}
        identifierClass={'data-asset-type'}
      />
      {isFieldPhotosEnabled(key, fieldSettings) && <SpaceProfilePhotoUpload isReadOnly={isReadOnly} guid={form.guid} fieldKey={key} spaceProfilePhotos={spaceProfilePhotos} setSpaceProfilePhotos={setSpaceProfilePhotos} />}
    </SurveyableField>
  );
}

const renderInput = (key, form, formSchema, fieldSettings, updateField, saveForm, isReadOnly, errors, setErrors, spaceProfilePhotos, setSpaceProfilePhotos) => {
  const schema = get(formSchema, key); // retrieve the schema values that defines the input
  if (!schema) console.error(`renderInput - No schema found`, { key, formSchema, form })

  if (!isFieldEnabled(key, schema.enabled, fieldSettings)) return null;

  if (schema.type === 'boolean') return renderBoolean(key, form, formSchema, fieldSettings, updateField, saveForm);
  const value = get(form, key); // retreive the value from the space profile record
  const fieldKey = key.split('.').pop();
  const formKey = key.split('.').slice(0, key.split('.').length - 1).join('.');
  const isValid = value && schema.validation ? String(value).match(schema.validation) : true;
  const hasError = Array.isArray(errors) && !!errors.find(_ => _.key === key);
  const required = fieldSettings ? isFieldRequired(key, fieldSettings) || isFieldSoftRequired(key, fieldSettings) : schema.required;

  return (
    <SurveyableField form={form}
      formKey={formKey}
      isReadOnly={isReadOnly}
      isSurveyed={isFieldSurveyed(key, fieldSettings)}
      fieldKey={key}
      updateField={updateField}
      saveForm={saveForm}>
      <AdminInput
        id={key}
        label={required ? `${schema.label} *` : schema.label}
        placeholder={isReadOnly ? null : schema.placeholder}
        value={value}
        validationmessage={intl.formatMessage({ id: 'AdminInput.validationMessage' }, { label: schema.label })}
        onChange={e => updateField(fieldKey, e, get(form, formKey), false, key)}
        isInvalid={!isValid || hasError}
        type={schema.type}
        readOnly={isReadOnly}
        required={required}
        onBlur={() => saveForm()}
        customstyle={'mt-3 mb-3'}
      />
      {isFieldPhotosEnabled(key, fieldSettings) && <SpaceProfilePhotoUpload isReadOnly={isReadOnly} guid={form.guid} fieldKey={key} spaceProfilePhotos={spaceProfilePhotos} setSpaceProfilePhotos={setSpaceProfilePhotos} />}
    </SurveyableField>
  );
}

const renderField = {
  'text': renderInput,
  'measurement': renderMeasurementInput,
  'boolean': renderBoolean,
  'number': renderInput,
  'date': renderInput,
  'textarea': renderInput,
  'select': renderSelect
}

const hasEnabledFields = (keys, formSchema, fieldSettings) => {
  const hasEnabledFields = keys.map(key => {
    const defaultEnabled = get(formSchema, `${key}.enabled`, true) === false ? false : true;
    return !fieldSettings ? defaultEnabled : get(fieldSettings, `${key}.enabled`, defaultEnabled);
  }).some(_ => _);
  return hasEnabledFields;
}

const getValue = (key, data, type, fieldSettings, spaceProfilePhotos, setAssetImagesState) => {
  let value = getNestedElement(data, key);
  if (value === null || value === undefined) return;
  if (type === 'boolean') return <div style={{display: 'flex'}}><div>{value ? t('ui.yes') : t('ui.no')}</div><ViewProfilePhoto spaceProfilePhotos={spaceProfilePhotos} fieldKey={key} setAssetImagesState={setAssetImagesState} fieldSettings={fieldSettings} /></div>;
  if (type === 'measurement') value = `${Math.floor(value / 12)}' ${value % 12}"`;
  return <div style={{display: 'flex'}}><div>{value}</div><ViewProfilePhoto spaceProfilePhotos={spaceProfilePhotos} fieldKey={key} setAssetImagesState={setAssetImagesState} fieldSettings={fieldSettings} /></div>;
}

const isFieldVisible = (key, fieldSettings) => fieldSettings ? fieldSettings && get(fieldSettings, `${key}.visible`) : true;

const renderValue = (key, data, fieldSettings, formSchema, spaceProfilePhotos, setAssetImagesState) => {
  if (!isFieldVisible(key, fieldSettings)) return;
  let schema = getNestedElement(formSchema, key);
  let append = get(schema, 'viewModeRenderOptions.append');
  let paren = get(data, get(schema, 'viewModeRenderOptions.paren', ''));
  let options = get(schema, 'viewModeRenderOptions.options', {});

  if (!schema) return null;
  const value = getValue(key, data, schema.type, fieldSettings, spaceProfilePhotos, setAssetImagesState);
  const label = schema.publicLabel || schema.label || key.replace(/_/g, ' ').replace(/(\b[a-z](?!\s))/g, _ => _.toUpperCase());
  if (schema && value) {
    return <div key={key} className={`${styles.infoItem} ${options.fullLength ? 'w-100' : ''}`}><b>{label}:</b> {value}{append} {paren ? ` (${paren})` : null} </div>;
  }
  return null;
}

const renderCompoundValue = (key, data, fieldSettings, displayKey, spaceProfilePhotos, setAssetImagesState) => {
  const front = isFieldVisible(`${key}.front`, fieldSettings) && getValue(`${key}.front`, data, 'measurement', fieldSettings, spaceProfilePhotos, setAssetImagesState);
  const back = isFieldVisible(`${key}.back`, fieldSettings) && getValue(`${key}.back`, data, 'measurement', fieldSettings, spaceProfilePhotos, setAssetImagesState);
  const center = isFieldVisible(`${key}.center`, fieldSettings) && getValue(`${key}.center`, data, 'measurement', fieldSettings, spaceProfilePhotos, setAssetImagesState);

  if (!front && !back && !center) return;

  return (
    <div key={key} className={styles.infoItem}>
      <div><b>{displayKey}:</b></div>
      <div style={{display: 'flex'}}>
        {front ? <div style={{display: 'flex', alignItems: 'flex-start', marginRight: '0.5em' }}> {t('assetSchema.front')}: {getValue(`${key}.front`, data, 'measurement')} <ViewProfilePhoto spaceProfilePhotos={spaceProfilePhotos} fieldKey={`${key}.front`} setAssetImagesState={setAssetImagesState} fieldSettings={fieldSettings} /></div> : null}{' '}
        {back ? <div style={{display: 'flex', alignItems: 'flex-start', marginRight: '0.5em'}}>{t('assetSchema.back')}: {getValue(`${key}.back`, data, 'measurement')} <ViewProfilePhoto spaceProfilePhotos={spaceProfilePhotos} fieldKey={`${key}.back`} setAssetImagesState={setAssetImagesState} fieldSettings={fieldSettings} /></div> : null}{' '}
        {center ? <div style={{display: 'flex', alignItems: 'flex-start', marginRight: '0.5em'}}>{t('assetSchema.center')}: {getValue(`${key}.center`, data, 'measurement')} <ViewProfilePhoto spaceProfilePhotos={spaceProfilePhotos} fieldKey={`${key}.center`} setAssetImagesState={setAssetImagesState} fieldSettings={fieldSettings} /></div> : null}{' '}
      </div>
    </div>
  );
}

export {
  renderBoolean,
  renderMeasurementInput,
  renderInput,
  renderField,
  renderSelect,
  hasEnabledFields,
  renderValue,
  renderCompoundValue
}